import { useCallback, useEffect, useMemo, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { finalizeComputationRun, listComputationRuns } from '../../api/calc'
import { linkEvidenceItemToRequirement, listEvidenceRequirements, waiveEvidenceRequirement } from '../../api/evidence'
import { ApiError, type ProblemViolation } from '../../api/problem'
import { label } from '../../lib/labels'
import type { ComputationRunView } from '../../types/calc'
import type { EvidenceItemView, EvidenceRequirementView } from '../../types/evidence'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'

export interface EvidenceRequirementsSectionProps {
  filing: ProgramFilingDetailView
  /** Every ACCEPTED (promoted) `EvidenceItem` on this Case — the only ones eligible to satisfy a
   * critical requirement (`EvidenceLinkService` rejects a draft/quarantined item, 422). */
  acceptedItems: EvidenceItemView[]
}

const REQUIREMENT_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  satisfied: 'success',
  waived: 'muted',
  na: 'neutral',
  partial: 'warning',
  not_started: 'danger',
}

const CRITICALITY_VARIANT: Record<string, StatusPillVariant> = {
  critical: 'danger',
  recommended: 'info',
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

function isTerminal(status: string): boolean {
  return status === 'satisfied' || status === 'waived' || status === 'na'
}

/**
 * One `EvidenceRequirement` row — status, and (while not yet terminal) the two real actions that
 * change it: attach an already-accepted `EvidenceItem` (satisfies it, FR-M14.3) or waive it with a
 * mandatory reason (FR-M14.5). `forceWaiveOpen` lets the finalize-readiness block below open this
 * exact row's waive control directly from a blocking violation, rather than making the user hunt
 * for it in the table.
 */
function RequirementRow({
  requirement,
  acceptedItems,
  forceWaiveOpen,
  onChanged,
}: {
  requirement: EvidenceRequirementView
  acceptedItems: EvidenceItemView[]
  forceWaiveOpen: boolean
  onChanged: () => void
}) {
  const [selectedItemId, setSelectedItemId] = useState('')
  const [waiveOpen, setWaiveOpen] = useState(forceWaiveOpen)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)

  useEffect(() => {
    if (forceWaiveOpen) setWaiveOpen(true)
  }, [forceWaiveOpen])

  const terminal = isTerminal(requirement.status)

  async function handleAttach() {
    if (!selectedItemId) return
    setBusy(true)
    setRowError(null)
    try {
      await linkEvidenceItemToRequirement(selectedItemId, requirement.id)
      setSelectedItemId('')
      onChanged()
    } catch (err) {
      setRowError(errorMessage(err, 'Could not attach this evidence item.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleWaive() {
    if (reason.trim().length < 10) return
    setBusy(true)
    setRowError(null)
    try {
      await waiveEvidenceRequirement(requirement.id, reason.trim())
      setReason('')
      setWaiveOpen(false)
      onChanged()
    } catch (err) {
      setRowError(errorMessage(err, 'Could not waive this requirement.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <tr>
        <td style={{ fontFamily: 'monospace', fontSize: 'var(--iw-size-xs)' }}>{requirement.key}</td>
        <td>{requirement.label}</td>
        <td>
          <StatusPill
            variant={CRITICALITY_VARIANT[requirement.criticality] ?? 'neutral'}
            label={label('evidenceRequirementCriticality', requirement.criticality)}
          />
        </td>
        <td>
          <StatusPill
            variant={REQUIREMENT_STATUS_VARIANT[requirement.status] ?? 'neutral'}
            label={label('evidenceRequirementStatus', requirement.status)}
          />
          {requirement.status === 'waived' && requirement.waiverReason ? (
            <div style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)', marginTop: 2 }}>
              {requirement.waiverReason}
            </div>
          ) : null}
        </td>
        <td>
          {terminal ? (
            <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
              <div style={{ display: 'flex', gap: 'var(--iw-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  className="iw-select"
                  aria-label={`Evidence item to attach to ${requirement.label}`}
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  disabled={acceptedItems.length === 0 || busy}
                >
                  <option value="">
                    {acceptedItems.length === 0 ? 'No accepted evidence items yet' : '— choose an accepted item —'}
                  </option>
                  {acceptedItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {label('evidenceType', item.type)} · {item.hash.slice(0, 15)}…
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="iw-btn iw-btn--sm"
                  disabled={!selectedItemId || busy}
                  onClick={() => void handleAttach()}
                >
                  Attach
                </button>
                <button
                  type="button"
                  className="iw-btn iw-btn--sm iw-btn--ghost"
                  aria-expanded={waiveOpen}
                  onClick={() => setWaiveOpen((v) => !v)}
                >
                  Waive
                </button>
              </div>
              {waiveOpen ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                  <label className="iw-field-label" htmlFor={`waive-reason-${requirement.id}`}>
                    Waiver reason (required, min 10 characters)
                  </label>
                  <textarea
                    id={`waive-reason-${requirement.id}`}
                    className="iw-input"
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div>
                    <button
                      type="button"
                      className="iw-btn iw-btn--sm iw-btn--primary"
                      disabled={reason.trim().length < 10 || busy}
                      onClick={() => void handleWaive()}
                    >
                      {busy ? 'Waiving…' : 'Confirm waiver'}
                    </button>
                  </div>
                </div>
              ) : null}
              {rowError ? <InlineAlert severity="error">{rowError}</InlineAlert> : null}
            </div>
          )}
        </td>
      </tr>
    </>
  )
}

/**
 * Finalize readiness for one filing (FR-M14.5) — the real gate, not a client-side re-derivation:
 * clicking Finalize calls the actual `POST /computation-runs/{id}/finalizations`. A blocked
 * attempt's `409 evidence-requirements-unsatisfied` violations are rendered verbatim, each with a
 * one-click "Waive" that opens the exact blocking requirement's row above. There is no separate
 * "check readiness, without finalizing" endpoint on this build — finalizing IS the check; when
 * every critical requirement is satisfied/waived, the same click both passes the gate and
 * finalizes the run (a real, one-way action, never a dead button).
 */
function FinalizeReadiness({
  filing,
  onBlocked,
  onFinalized,
}: {
  filing: ProgramFilingDetailView
  onBlocked: (violations: ProblemViolation[]) => void
  onFinalized: () => void
}) {
  const [runs, setRuns] = useState<ComputationRunView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<'idle' | 'finalized' | 'blocked'>('idle')
  const [blockingViolations, setBlockingViolations] = useState<ProblemViolation[]>([])

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listComputationRuns(filing.id)
      .then((page) => {
        setRuns(page.items)
        setState('ready')
      })
      .catch((err) => {
        setError(errorMessage(err, 'Could not load computation runs for this filing.'))
        setState('error')
      })
  }, [filing.id])

  useEffect(() => {
    load()
  }, [load])

  const candidate = useMemo(() => runs.find((r) => r.finalizedAt === null) ?? null, [runs])

  async function handleFinalize() {
    if (!candidate) return
    setBusy(true)
    setOutcome('idle')
    setBlockingViolations([])
    try {
      await finalizeComputationRun(candidate.id)
      setOutcome('finalized')
      load()
      onFinalized()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.problem.violations) {
        setBlockingViolations(err.problem.violations)
        setOutcome('blocked')
        onBlocked(err.problem.violations)
      } else {
        setError(errorMessage(err, 'Could not finalize this computation run.'))
      }
    } finally {
      setBusy(false)
    }
  }

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Loading finalize readiness…</p>
  }
  if (state === 'error') {
    return <InlineAlert severity="error">{error}</InlineAlert>
  }

  if (runs.length === 0) {
    return (
      <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
        No computation run recorded yet for this filing — nothing to finalize.
      </p>
    )
  }

  if (!candidate) {
    return <StatusPill variant="success" label="Every computation run on this filing is already finalized" />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          Ready to finalize <EntityRef kind="record" id={candidate.id} name={`computation run (${candidate.kind})`} />:
        </span>
        <button type="button" className="iw-btn iw-btn--sm iw-btn--primary" disabled={busy} onClick={() => void handleFinalize()}>
          {busy ? 'Checking…' : 'Finalize'}
        </button>
      </div>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {outcome === 'finalized' ? (
        <InlineAlert severity="success">Finalized — the FR-M14.5 evidence gate passed with zero blocking requirements.</InlineAlert>
      ) : null}
      {outcome === 'blocked' ? (
        <InlineAlert severity="warning">
          <div>
            Finalize was refused — {blockingViolations.length} critical evidence requirement
            {blockingViolations.length === 1 ? '' : 's'} still unsatisfied and unwaived:
          </div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 'var(--iw-space-4)' }}>
            {blockingViolations.map((v) => (
              <li key={v.field}>
                {v.message ?? v.field} <span style={{ fontFamily: 'monospace', fontSize: 'var(--iw-size-xs)' }}>({v.field})</span>
              </li>
            ))}
          </ul>
        </InlineAlert>
      ) : null}
    </div>
  )
}

/**
 * The Case-workspace Evidence tab's per-filing requirement + finalize-readiness block (evidence-
 * vault story, FR-M14.3/4/5): the real requirement list (`GET .../evidence-requirements`), the
 * real satisfy path (attach an accepted item -&gt; `POST .../links`), the real waive path (`POST
 * .../waive`), and the real finalize gate (`POST /computation-runs/{id}/finalizations`) — never a
 * client-side re-derivation of any of these.
 */
export function EvidenceRequirementsSection({ filing, acceptedItems }: EvidenceRequirementsSectionProps) {
  const [requirements, setRequirements] = useState<EvidenceRequirementView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [forceWaiveKey, setForceWaiveKey] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listEvidenceRequirements(filing.id)
      .then((rows) => {
        setRequirements(rows)
        setState('ready')
      })
      .catch((err) => {
        setError(errorMessage(err, 'Could not load evidence requirements for this filing.'))
        setState('error')
      })
  }, [filing.id])

  useEffect(() => {
    load()
  }, [load])

  function handleBlocked(violations: ProblemViolation[]) {
    const first = violations[0]
    if (first) setForceWaiveKey(first.field)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <EntityRef kind="programFiling" id={filing.id} />
      </h4>

      {state === 'loading' ? <p style={{ color: 'var(--iw-text-muted)' }}>Loading evidence requirements…</p> : null}
      {state === 'error' ? <InlineAlert severity="error">{error}</InlineAlert> : null}

      {state === 'ready' && requirements.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          No evidence requirements defined yet for this filing.
        </p>
      ) : null}

      {state === 'ready' && requirements.length > 0 ? (
        <table className="iw-table" aria-label={`Evidence requirements for filing ${filing.id}`}>
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Requirement</th>
              <th scope="col">Criticality</th>
              <th scope="col">Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {requirements.map((requirement) => (
              <RequirementRow
                key={requirement.id}
                requirement={requirement}
                acceptedItems={acceptedItems}
                forceWaiveOpen={forceWaiveKey === requirement.key}
                onChanged={load}
              />
            ))}
          </tbody>
        </table>
      ) : null}

      <div>
        <h5 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Finalize readiness</h5>
        <FinalizeReadiness filing={filing} onBlocked={handleBlocked} onFinalized={load} />
      </div>
    </div>
  )
}
